/**
 * SitePick front — 比較トレイ（docs/06 §3 / §36〜§38）
 *
 * compare が 1 件以上のとき表示。件数 / 簡易表示 / 個別削除 / 全削除 / `/compare/` CTA。
 * 表示時に勝手に focus を移さない。キーボードで各操作が可能。
 * markup の器はテーマ（`parts/compare-tray.php`）。中身はここで組む（textContent / createElement）。
 */

import { compareStore } from "./compare-store.js";
import { fetchBatchSites } from "./site-api.js";
import { announce } from "./live-region.js";

let rootEl = null;
let listEl = null;
let countEl = null;
/** @type {Map<number, string>} id -> name のキャッシュ */
const nameCache = new Map();
let fetchToken = 0;

function msg(key, fallback) {
  const m = (window.SITEPICK && window.SITEPICK.messages) || {};
  return typeof m[key] === "string" && m[key] ? m[key] : fallback;
}

function compareUrl() {
  const cfg = window.SITEPICK || {};
  if (cfg.urls && typeof cfg.urls.compare === "string" && cfg.urls.compare) {
    return cfg.urls.compare;
  }
  return "/compare/";
}

/**
 * 1 行を組む。
 *
 * @param {number} id
 * @returns {HTMLLIElement}
 */
function renderItem(id) {
  const li = document.createElement("li");
  li.className = "p-compare-tray__item";

  const name = document.createElement("span");
  name.className = "p-compare-tray__name";
  name.textContent = nameCache.has(id)
    ? nameCache.get(id)
    : msg("loading", "読み込み中…");
  li.appendChild(name);

  const remove = document.createElement("button");
  remove.type = "button";
  remove.className = "p-compare-tray__remove js-sp-tray-remove";
  remove.setAttribute("data-sp-id", String(id));
  const label = nameCache.get(id) || `#${id}`;
  remove.setAttribute(
    "aria-label",
    msg("compareRemoveLabel", "%s を比較から削除").replace("%s", label)
  );
  remove.textContent = "×";
  li.appendChild(remove);

  return li;
}

/**
 * トレイを現在の compare 状態で描画する。
 */
/** トレイの実高さを CSS 変数に反映（下部固定要素の段積み調整用）。 */
function syncTrayOffset() {
  const root = document.documentElement;
  if (rootEl && !rootEl.hidden) {
    root.style.setProperty("--sp-tray-h", `${rootEl.offsetHeight}px`);
    root.classList.add("has-sp-compare-tray");
  } else {
    root.style.removeProperty("--sp-tray-h");
    root.classList.remove("has-sp-compare-tray");
  }
}

function render() {
  if (!rootEl) {
    return;
  }
  const ids = compareStore.get();

  if (ids.length === 0) {
    rootEl.hidden = true;
    if (listEl) {
      listEl.textContent = "";
    }
    syncTrayOffset();
    return;
  }

  rootEl.hidden = false;
  if (countEl) {
    countEl.textContent = String(ids.length);
  }
  if (listEl) {
    listEl.textContent = "";
    ids.forEach((id) => listEl.appendChild(renderItem(id)));
  }

  // 足りない名前を取りに行く。
  const missing = ids.filter((id) => !nameCache.has(id));
  if (missing.length > 0) {
    const myToken = (fetchToken += 1);
    fetchBatchSites(ids)
      .then((sites) => {
        if (myToken !== fetchToken) {
          return;
        }
        sites.forEach((site) => {
          if (site && Number.isInteger(site.id)) {
            nameCache.set(site.id, site.name || `#${site.id}`);
          }
        });
        render();
      })
      .catch(() => {
        /* 名前が取れなくても件数と削除は機能する */
      });
  }

  syncTrayOffset();
}

/**
 * トレイ内の操作。
 *
 * @param {Event} event
 */
function onClick(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) {
    return;
  }
  const removeBtn = target.closest(".js-sp-tray-remove");
  if (removeBtn instanceof HTMLElement) {
    const id = parseInt(removeBtn.getAttribute("data-sp-id") || "0", 10);
    if (Number.isInteger(id) && id > 0) {
      compareStore.remove(id);
      announce(msg("compareRemoved", "比較から削除しました"));
    }
    return;
  }
  if (target.closest(".js-sp-tray-clear")) {
    compareStore.clear();
    announce(msg("compareCleared", "比較をすべて解除しました"));
  }
}

/**
 * 初期化。
 *
 * @returns {void}
 */
export function initCompareTray() {
  rootEl = document.querySelector("[data-sp-compare-tray]");
  if (!rootEl) {
    return;
  }
  listEl = rootEl.querySelector("[data-sp-compare-tray-list]");
  countEl = rootEl.querySelector("[data-sp-compare-tray-count]");

  const cta = rootEl.querySelector("[data-sp-compare-tray-cta]");
  if (cta instanceof HTMLAnchorElement) {
    cta.href = compareUrl();
  }

  rootEl.addEventListener("click", onClick);
  compareStore.subscribe(render);

  let resizeRaf = 0;
  window.addEventListener(
    "resize",
    () => {
      window.cancelAnimationFrame(resizeRaf);
      resizeRaf = window.requestAnimationFrame(syncTrayOffset);
    },
    { passive: true }
  );

  render();
}
